import React from 'react';
import {
  Autocomplete,
  Avatar,
  Backdrop,
  Box,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fetchGet } from '../../api/common';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { resizeImage } from '../../api/cloudinary';
import { getAvatarSrc } from '../room/utils';

type Props = {
  headerHeight: number;
  handleSelect: (user: any) => void;
  hideBackdrop?: boolean;
  fullWidth?: boolean;
};

export const SearchBarMain = ({
  headerHeight,
  handleSelect,
  hideBackdrop,
  fullWidth,
}: Props) => {
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const searchbarWidth = fullWidth ? '500px' : '250px';
  const searchbarFocusedWidth = xsBreakpoint ? '90%' : '500px';

  const navigate = useNavigate();
  const [isFocused, setIsFocused] = React.useState(false);
  const [options, setOptions] = React.useState<readonly any[]>([]);

  const baseZIndex = 1000; // or any other base value that doesn't conflict with other z-indexes in your app

  const searchUsers = async (searchTerm: string) => {
    if (!searchTerm.length) {
      setOptions([]);
      return;
    }
    const res = await fetchGet(`/users/search?term=${searchTerm}`);
    const sorted = res.sort((a, b) => a.username.localeCompare(b.username));
    setOptions(sorted);
  };

  const handlePlayerSelect = (user: any) => {
    if (!user) return;
    setIsFocused(false);
    handleSelect(user);
    // navigate(`/profile/overview/${user._id}`);
  };

  const debouncedTyping = debounce((term: string) => {
    searchUsers(term);
  }, 300);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const CustomPaper = (props) => (
    <Paper
      {...props}
      sx={{
        zIndex: baseZIndex + 1, // This ensures the dropdown appears above the backdrop
        minWidth: searchbarWidth,
        width: isFocused ? searchbarFocusedWidth : searchbarWidth,
        transition: 'width 0.3s',
      }}
    />
  );

  const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1),
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  return (
    <>
      {!hideBackdrop && xsBreakpoint ? (
        <Backdrop
          sx={{
            zIndex: baseZIndex,
            color: '#fff',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: `${headerHeight - 25}px`,
          }}
          open={isFocused}
          onClick={handleBlur}
        />
      ) : null}
      <Autocomplete
        noOptionsText="No results"
        options={options}
        PaperComponent={CustomPaper}
        PopperComponent={({ children, ...props }) => (
          <Popper
            {...props}
            style={{
              transform: 'translateX(-50%)',
              width: searchbarFocusedWidth,
            }}
          >
            {children}
          </Popper>
        )}
        popupIcon={null}
        blurOnSelect
        size="small"
        sx={{
          width: isFocused ? searchbarFocusedWidth : searchbarWidth, // Set the width based on focus state
          transition: 'width 0.3s',
          alignItems: 'center',
          zIndex: 1,
          backgroundImage:
            'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))',
          backgroundColor: 'rgb(33, 33, 33)',
          borderRadius: '4px',
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onInputChange={(event, newInputValue) => {
          debouncedTyping(newInputValue);
        }}
        onChange={(event: any, newValue: any) => {
          handlePlayerSelect(newValue);
        }}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.username}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={params.InputProps.ref}
            variant="outlined"
            placeholder="Search Players"
            autoFocus
            sx={{
              boxShadow: '0 0 5px rgba(33, 150, 243, 0.5)',
              '& .MuiOutlinedInput-input': {
                fontSize: '0.875rem',
                padding: '0 6px',
                alignSelf: 'center',
              },
              '&:hover': {
                boxShadow: '0 0 5px #cda68c80',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#cda68cff',
                },
              },
              '&.Mui-focused': {
                boxShadow: '0 0 5px #cda68ce6, 0 0 10px #cda68ccc',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#cda68ce6',
                },
              },
            }}
            InputProps={{
              ...params.InputProps,

              startAdornment: (
                <>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
            {...props}
          >
            <Avatar
              alt={option.username}
              src={getAvatarSrc(
                option?.avatarPublicId,
                option?.avatarUrl,
                100,
                100
              )}
              sx={{ marginRight: 1, width: 32, height: 32, borderRadius: 1 }}
            />
            <Stack direction={'column'}>
              {option.username}
              <Typography variant="body2" color="gray">
                {option.fullName ? option.fullName : ''}
                {option.location ? ' from ' + option.location : ''}
              </Typography>
              {!option.fullName && !option.location ? (
                <Typography variant="body2" color="gray" fontStyle={'italic'}>
                  No additional information
                </Typography>
              ) : null}
            </Stack>
          </Box>
        )}
      />
      {!hideBackdrop ? (
        <Backdrop
          sx={{
            zIndex: baseZIndex,
            color: '#fff',
            position: 'fixed',
            top: `${xsBreakpoint ? headerHeight + 32 : headerHeight}px`, // The height of the header and possibly banners
            left: 0,
            right: 0,
            bottom: 0,
          }}
          open={isFocused}
          onClick={handleBlur}
        />
      ) : null}
    </>
  );
};
